import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import apiOffers from 'api/offers';
import SelectInput from 'components/layout/forms/SelectInput';
import TextInputField from 'components/layout/forms/TextInputField';
import ModalConfirm from 'components/layout/modals/ModalConfirm';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';

interface IDepartment {
  id: number;
  name: string;
}

/**
 * The department of the offer. A user who can edit the offers of several departments can
 * move the offer, e.g. when it was made for another department. A sales opportunity can be
 * given to any department (allDepartments), also when it is made (without offerId: the
 * department is chosen at once and saved with the opportunity).
 */
const OfferDepartment = ({
  allDepartments,
  offerId,
  organizationUnit,
  onMoved,
}: {
  allDepartments?: boolean;
  offerId?: number;
  organizationUnit: null | { id?: number; name: string };
  onMoved: (organizationUnit: IDepartment) => void;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([
    translations.offers,
    translations.common,
    translations.userManagement,
  ]);
  const [departments, setDepartments] = useState<IDepartment[]>([]);
  // The department chosen but not yet confirmed
  const [target, setTarget] = useState<IDepartment | null>(null);

  useEffect(() => {
    apiOffers.departments
      .get(true, allDepartments)
      .then(setDepartments)
      .catch(() => setDepartments([]));
  }, [allDepartments]);

  const label = t(`${translations.userManagement}:Organization unit`);
  const currentId = organizationUnit?.id;
  const canMove =
    currentId !== undefined &&
    departments.length > 1 &&
    departments.some((department) => department.id === currentId);

  if (!canMove) {
    return (
      <TextInputField
        disabled
        label={label}
        required
        value={organizationUnit?.name ?? ''}
      />
    );
  }

  const move = async () => {
    if (target === null || offerId === undefined) {
      return;
    }

    try {
      await apiOffers.patchOrganizationUnit(offerId, target.id);
      onMoved(target);
      dispatch(
        addMessage(
          t(`${translations.offers}:department.Moved`, {
            department: target.name,
          })
        )
      );
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:department.Move failed`)));
    }

    setTarget(null);
  };

  return (
    <>
      <ModalConfirm
        showing={target !== null}
        setShowing={() => setTarget(null)}
        header={t(`${translations.offers}:department.Confirm header`)}
        text={t(`${translations.offers}:department.Confirm text`, {
          department: target?.name ?? '',
        })}
        yesButton={t(`${translations.common}:Confirm`)}
        noButton={t(`${translations.common}:Cancel`)}
        confirm={move}
      />
      <SelectInput
        id="offer-department"
        key="offer-department"
        label={label}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const chosen =
            departments.find(
              (department) => department.id === parseInt(event.target.value, 10)
            ) ?? null;

          // A new opportunity is not moved: its department is saved with it
          if (offerId === undefined) {
            if (chosen !== null) {
              onMoved(chosen);
            }
            return;
          }

          setTarget(chosen);
        }}
        options={departments.map((department) => ({
          displayValue: department.name,
          value: department.id.toString(),
        }))}
        required
        value={currentId.toString()}
      />
    </>
  );
};

export default OfferDepartment;
